import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilmCard from "@/components/feature/FilmCard";
import { films } from "@/mocks/films";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";

export default function FilmDetail() {
  const { id } = useParams<{ id: string }>();
  const film = useMemo(() => films.find((f) => f.id === id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [shareUrl, setShareUrl] = useState("");

  const related = useMemo(() => {
    if (!film) return [];
    return films
      .filter((f) => f.id !== film.id)
      .map((f) => ({
        film: f,
        score:
          (f.category === film.category ? 2 : 0) +
          f.tags.filter((t) => film.tags.includes(t)).length +
          f.genres.filter((g) => film.genres.includes(g)).length,
      }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((item) => item.film);
  }, [film]);

  useSeo({
    title: film
      ? `${film.title} - 久久爱影视详情介绍，剧情简介与演员信息`
      : "影视详情 - 久久爱",
    description: film
      ? `《${film.title}》是${film.year}年上映的${film.region}${film.category}，由${film.director}执导，${film.actors.join("、")}主演。累计评分${film.rating.toFixed(1)}分。${film.summary}`
      : "久久爱影视详情页，展示作品剧情简介、主演信息与相关推荐。",
    keywords: film
      ? `${film.title},${film.title}剧情,${film.genres.join(",")},久久爱影视详情,${film.actors.join(",")}`
      : "影视详情,久久爱",
  });

  if (!film) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="flex max-w-md flex-col items-center gap-3 rounded-xl border border-background-200 bg-background-100 px-8 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </span>
            <h1 className="text-lg font-bold text-foreground-900">
              抱歉，当前影视作品信息不存在
            </h1>
            <p className="text-sm text-foreground-500">
              该作品可能已下架，或者链接有误。你可以返回首页继续浏览其他内容。
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-home-4-line text-lg"></i>
                返回首页
              </Link>
              <Link
                to="/category"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300"
              >
                <i className="ri-compass-3-line text-lg"></i>
                浏览分类
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const favorited = isFavorite(film.id);

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}/detail/${film.id}`.replace(
      /([^:]\/)\/+/g,
      "$1"
    );
    setShareUrl(url);
    setShareOpen(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动选择链接复制");
    }
    setShareOpen(false);
    window.setTimeout(() => setToast(""), 2400);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={film.backdrop}
              alt={`${film.title}主视觉背景`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-foreground-950/80 to-foreground-950/70"></div>
          </div>

          <div className="relative mx-auto w-full px-4 py-8 md:px-6 md:py-12">
            <div className="mx-auto max-w-5xl">
              <Breadcrumb
                items={[
                  { label: "影视分类", path: "/category" },
                  { label: film.category, path: `/category?type=${film.category}` },
                  { label: film.title },
                ]}
              />

              <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
                <div className="w-40 shrink-0 overflow-hidden rounded-lg bg-background-200 md:w-56">
                  <img
                    src={film.cover}
                    alt={`${film.title}影视封面`}
                    title={`${film.title} - 久久爱影视详情`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-primary-500 px-3 py-0.5 text-xs font-medium text-background-50">
                      {film.category}
                    </span>
                    {film.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-background-50/15 px-3 py-0.5 text-xs text-background-100"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="mt-3 text-2xl font-extrabold text-background-50 md:text-4xl font-heading">
                    {film.title}
                  </h1>

                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100">
                    <span className="flex items-center gap-1 text-lg font-bold text-accent-400">
                      <i className="ri-star-fill text-base"></i>
                      {film.rating.toFixed(1)}
                    </span>
                    <span>{film.year}</span>
                    <span>{film.region}</span>
                    <span>{film.genres.join(" / ")}</span>
                  </div>

                  <div className="mt-4 space-y-1.5 text-sm text-background-100">
                    <p>
                      <span className="text-background-300">导演：</span>
                      {film.director}
                    </p>
                    <p>
                      <span className="text-background-300">主演：</span>
                      {film.actors.join("、")}
                    </p>
                    <p>
                      <span className="text-background-300">上映日期：</span>
                      {film.releaseDate}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const added = toggleFavorite(film.id);
                        setToast(added ? "已加入我的收藏" : "已取消收藏");
                        window.setTimeout(() => setToast(""), 2000);
                      }}
                      className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                        favorited
                          ? "bg-accent-500 text-foreground-950 hover:bg-accent-600"
                          : "bg-background-50/10 text-background-50 hover:bg-background-50/20"
                      }`}
                    >
                      <i
                        className={`text-lg ${
                          favorited ? "ri-heart-3-fill" : "ri-heart-3-line"
                        }`}
                      ></i>
                      {favorited ? "已收藏" : "收藏"}
                    </button>
                    <button
                      type="button"
                      onClick={handleShare}
                      className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-share-forward-line text-lg"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-12">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
            <div className="flex-1 lg:max-w-3xl">
              <section>
                <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950 md:text-2xl font-heading">
                  <span className="h-5 w-1 rounded-full bg-accent-500 md:h-6"></span>
                  剧情简介
                </h2>
                <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                  {film.summary}
                </p>
              </section>

              <section className="mt-10">
                <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950 md:text-2xl font-heading">
                  <span className="h-5 w-1 rounded-full bg-accent-500 md:h-6"></span>
                  特色亮点
                </h2>
                <ul className="mt-4 flex flex-col gap-3">
                  {film.highlights.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                        <i className="ri-sparkling-2-line text-sm"></i>
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <aside className="w-full lg:w-72">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="text-sm font-bold text-foreground-950">作品信息</h3>
                <dl className="mt-4 flex flex-col gap-3 text-sm">
                  {[
                    { label: "类型", value: film.category },
                    { label: "题材", value: film.genres.join(" / ") },
                    { label: "地区", value: film.region },
                    { label: "年份", value: `${film.year} 年` },
                    { label: "导演", value: film.director },
                    { label: "主演", value: film.actors.join("、") },
                    { label: "评分", value: `${film.rating.toFixed(1)} 分` },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-3">
                      <dt className="w-14 shrink-0 text-foreground-500">{row.label}</dt>
                      <dd className="flex-1 text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-bold text-foreground-950">标签</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {film.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-14">
              <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950 md:text-2xl font-heading">
                <span className="h-5 w-1 rounded-full bg-accent-500 md:h-6"></span>
                相关推荐
              </h2>
              <p className="mt-1.5 text-xs text-foreground-500 md:text-sm">
                根据类型与标签为你匹配的相似作品
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
                {related.map((item) => (
                  <FilmCard key={item.id} film={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />

      {shareOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center anim-fade">
          <div
            className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
            onClick={() => setShareOpen(false)}
          ></div>
          <div className="relative w-[90%] max-w-md rounded-xl bg-background-50 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-foreground-950">分享作品</h3>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="mt-2 text-sm text-foreground-500">
              复制下方链接，分享给同样喜欢影视的朋友
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 p-2">
              <input
                readOnly
                value={shareUrl}
                className="w-full bg-transparent px-2 text-sm text-foreground-700 focus:outline-none"
              />
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300"
              >
                取消
              </button>
              <button
                type="button"
                onClick={copyLink}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-file-copy-line text-base"></i>
                复制链接
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[80] -translate-x-1/2 anim-fade">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
            <i className="ri-checkbox-circle-line text-accent-400"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}